/**
 * mediaBindRegistry — which elements are EMPTY media widgets that accept a
 * dragged asset (drop-to-fill), and for which grid item.
 *
 * Replaces the `data-media-bind` / `data-bind-grid` / `data-bind-item`
 * attributes the AddDrag hit-test used to read via `closest()`:
 * react-strict-dom's html.div forwards no `data-*` hook beyond `data-testid`,
 * so the widgets register their root element here instead (the same pattern
 * as FlowCanvasFrame's registerFlowCanvas / isInFlowCanvas).
 */
import { useCallback } from "react";

export type MediaBindKind = "image" | "video" | "audio";

export interface MediaBindInfo {
  readonly kind: MediaBindKind;
  readonly gridId: string;
  readonly itemId: string;
}

const binds = new WeakMap<Element, MediaBindInfo>();

/** Mark `el` as an empty media widget. Returns the unregister function. */
export function registerMediaBind(
  el: Element,
  info: MediaBindInfo,
): () => void {
  binds.set(el, info);
  return () => {
    // A later registration of the same element owns the entry now.
    if (binds.get(el) === info) binds.delete(el);
  };
}

/** The nearest registered empty media widget at or above `el`, or null. */
export function mediaBindAt(
  el: Element | null | undefined,
): MediaBindInfo | null {
  for (let node = el ?? null; node; node = node.parentElement) {
    const info = binds.get(node);
    if (info) return info;
  }
  return null;
}

/** A callback ref registering its element while `empty` (React 19: the
 *  returned function is the ref cleanup). `onElement` also receives the
 *  element (null on detach) for widgets that keep their own ref to it. */
export function useMediaBindRef<T extends Element>(
  kind: MediaBindKind,
  gridId: string,
  itemId: string,
  empty: boolean,
  onElement?: (el: T | null) => void,
): (el: T | null) => (() => void) | undefined {
  return useCallback(
    (el: T | null) => {
      onElement?.(el);
      if (!el) return undefined;
      const unregister = empty
        ? registerMediaBind(el, { kind, gridId, itemId })
        : undefined;
      return () => {
        unregister?.();
        onElement?.(null);
      };
    },
    [kind, gridId, itemId, empty, onElement],
  );
}
